/**
 * O que a vitrine mostra, em que ordem e com que nome.
 *
 * `unit` espelha o `unit` do catálogo
 * (`apps/api/src/modules/entitlements/catalog/feature-catalog.ts`) e é o que
 * diz ao formatador (`formatLimit`, em `./format-limit.ts`) se o número é
 * byte, hora ou dia. Deve acompanhar a definição da API.
 *
 * Exceção proposital: `assistant.whatsapp` está com `unit: "day"` mesmo o
 * catálogo de hoje tendo `unit: null` (gate puro) — uma task posterior deste
 * mesmo plano muda o catálogo para `modes: ['trialDays'], unit: 'day'`.
 */
import { formatLimit } from "./format-limit";
import {
  SHOWCASE_COUNT_LABELS_COPY,
  SHOWCASE_FEATURE_LABELS_COPY,
  SHOWCASE_LIMIT_COPY,
} from "./showcase-features.copy";
import type { PlanEntitlements } from "./entitlement.schema";
import type { ShowcaseFeature, ShowcaseGroup } from "./showcase-features.types";

export const SHOWCASE_GROUPS: readonly ShowcaseGroup[] = [
  "Conteúdo",
  "Campanhas e publi",
  "Finanças e métricas",
  "Assistente Tãmi",
  "Equipe e arquivos",
];

export const SHOWCASE_FEATURES: readonly ShowcaseFeature[] = [
  { key: "content.tasks", get label() { return SHOWCASE_FEATURE_LABELS_COPY["content.tasks"]; }, group: "Conteúdo", unit: "task" },
  { key: "content.scripts", get label() { return SHOWCASE_FEATURE_LABELS_COPY["content.scripts"]; }, group: "Conteúdo", unit: "script" },
  { key: "calendar.events", get label() { return SHOWCASE_FEATURE_LABELS_COPY["calendar.events"]; }, group: "Conteúdo", unit: "event" },
  { key: "notes.items", get label() { return SHOWCASE_FEATURE_LABELS_COPY["notes.items"]; }, group: "Conteúdo", unit: "note" },

  { key: "campaigns.jobs", get label() { return SHOWCASE_FEATURE_LABELS_COPY["campaigns.jobs"]; }, group: "Campanhas e publi", unit: "campaign" },
  { key: "calculator.calculations", get label() { return SHOWCASE_FEATURE_LABELS_COPY["calculator.calculations"]; }, group: "Campanhas e publi", unit: "calculation" },
  { key: "calculator.pdf", get label() { return SHOWCASE_FEATURE_LABELS_COPY["calculator.pdf"]; }, group: "Campanhas e publi", unit: "pdf" },
  { key: "calculator.whitelabel", get label() { return SHOWCASE_FEATURE_LABELS_COPY["calculator.whitelabel"]; }, group: "Campanhas e publi", unit: null },

  { key: "finance.entries", get label() { return SHOWCASE_FEATURE_LABELS_COPY["finance.entries"]; }, group: "Finanças e métricas", unit: "day" },
  { key: "social.profiles", get label() { return SHOWCASE_FEATURE_LABELS_COPY["social.profiles"]; }, group: "Finanças e métricas", unit: "profile" },
  { key: "social.syncInterval", get label() { return SHOWCASE_FEATURE_LABELS_COPY["social.syncInterval"]; }, group: "Finanças e métricas", unit: "hour" },
  { key: "social.history", get label() { return SHOWCASE_FEATURE_LABELS_COPY["social.history"]; }, group: "Finanças e métricas", unit: "day" },

  { key: "assistant.whatsapp", get label() { return SHOWCASE_FEATURE_LABELS_COPY["assistant.whatsapp"]; }, group: "Assistente Tãmi", unit: "day" },
  { key: "assistant.messages", get label() { return SHOWCASE_FEATURE_LABELS_COPY["assistant.messages"]; }, group: "Assistente Tãmi", unit: "message", within: "assistant.whatsapp" },
  { key: "assistant.audioMinutes", get label() { return SHOWCASE_FEATURE_LABELS_COPY["assistant.audioMinutes"]; }, group: "Assistente Tãmi", unit: "minute", get countLabel() { return SHOWCASE_COUNT_LABELS_COPY.audioMinutes; }, within: "assistant.whatsapp" },
  // Independe do assistente: no Start os avisos seguem depois dos 30 dias (TAM-316).
  { key: "assistant.proactive", get label() { return SHOWCASE_FEATURE_LABELS_COPY["assistant.proactive"]; }, group: "Assistente Tãmi", unit: null },

  { key: "team.members", get label() { return SHOWCASE_FEATURE_LABELS_COPY["team.members"]; }, group: "Equipe e arquivos", unit: "member", get countLabel() { return SHOWCASE_COUNT_LABELS_COPY.teamMembers; } },
  { key: "storage.total", get label() { return SHOWCASE_FEATURE_LABELS_COPY["storage.total"]; }, group: "Equipe e arquivos", unit: "bytes" },
  { key: "storage.perFile", get label() { return SHOWCASE_FEATURE_LABELS_COPY["storage.perFile"]; }, group: "Equipe e arquivos", unit: "bytes" },
];

/**
 * O valor que a vitrine mostra para um recurso do plano: `formatLimit` mais o
 * prazo do recurso em `within`, quando ele tem prazo ("ilimitado nos 30 dias").
 * O "/mês" sai nesse caso — dentro da janela ele não acrescenta nada.
 */
export function formatShowcaseLimit(
  entitlements: PlanEntitlements,
  feature: ShowcaseFeature,
): string {
  const resolved = entitlements[feature.key];
  if (!resolved) return SHOWCASE_LIMIT_COPY.missing;

  const valor = formatLimit(resolved, feature.unit, feature.countLabel);
  const prazo = feature.within ? entitlements[feature.within] : undefined;

  if (!prazo) return valor;
  if (!resolved.enabled || resolved.trial?.enabled) return valor;
  if (!prazo.enabled || prazo.mode !== "trialDays" || prazo.limit === null) return valor;

  const dias = SHOWCASE_LIMIT_COPY.days(prazo.limit);
  return SHOWCASE_LIMIT_COPY.withDeadline(valor.replace(/ \/mês$/, ""), dias);
}

/** Agrupado na ordem declarada em `SHOWCASE_GROUPS`, para a matriz. */
export function featuresByGroup(): Array<[ShowcaseGroup, ShowcaseFeature[]]> {
  return SHOWCASE_GROUPS.map((group) => [
    group,
    SHOWCASE_FEATURES.filter((feature) => feature.group === group),
  ]);
}
